import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { ClientEnv } from 'constants/Chat.constants';

import { projectInit, resetChat } from 'actions';
import type { TurnMetadata } from 'api/resources/conversation/types';
import type { ChatMessage } from 'api/types';

export type AgentResponse = {
  response: string;
  userInput: string;
  metadata?: TurnMetadata;
};

interface VariantState {
  conversationId: string | null;
  messages: ChatMessage[];
  agentResponses: AgentResponse[];
  isLoading: boolean;
  isSendingMessage: boolean;
  isEnded: boolean;
  isErrored: boolean;
  messageDraft: string;
  endTurn: ChatMessage | null;
  isWaitingSlowResponse: boolean;
  asrLanguageCode: string | null;
  ttsLanguageCode: string | null;
  variantOverwriteWarningDismissed: boolean;
}

export interface ChatState {
  draft: Record<string, VariantState>;
  sandbox: Record<string, VariantState>;
  'pre-release': Record<string, VariantState>;
  live: Record<string, VariantState>;
  draftArtifactVersions: {
    artifact?: string;
    lambdaDeployment?: string;
  };
}

export const initialConversationState: VariantState = {
  conversationId: null,
  messages: [],
  agentResponses: [],
  isLoading: false,
  isSendingMessage: false,
  isEnded: false,
  isErrored: false,
  messageDraft: '',
  endTurn: null,
  isWaitingSlowResponse: false,
  asrLanguageCode: null,
  ttsLanguageCode: null,
  variantOverwriteWarningDismissed: false,
};

const initialState: ChatState = {
  draft: {},
  sandbox: {},
  'pre-release': {},
  live: {},
  draftArtifactVersions: {
    artifact: undefined,
    lambdaDeployment: undefined,
  },
};

const resetDraftArtifactVersionsHelper = (state: ChatState) => {
  state.draftArtifactVersions = {
    artifact: undefined,
    lambdaDeployment: undefined,
  };
};

type ClientEnvPayload = {
  clientEnv: ClientEnv;
  variantName: string;
};

const testAgentSlice = createSlice({
  name: 'testAgent',
  initialState,
  reducers: {
    setConversationId: (
      state,
      {
        payload: { clientEnv, variantName, conversationId },
      }: PayloadAction<{ conversationId: string } & ClientEnvPayload>,
    ) => {
      if (!state[clientEnv][variantName]) {
        state[clientEnv][variantName] = { ...initialConversationState };
      }
      state[clientEnv][variantName].conversationId = conversationId;
    },
    setIsLoading: (
      state,
      {
        payload: { clientEnv, variantName, isLoading },
      }: PayloadAction<{ isLoading: boolean } & ClientEnvPayload>,
    ) => {
      if (!state[clientEnv][variantName]) {
        state[clientEnv][variantName] = { ...initialConversationState };
      }
      state[clientEnv][variantName].isLoading = isLoading;
    },
    setVariantOverwriteWarningDismissed: (
      state,
      {
        payload: { clientEnv, variantName, variantOverwriteWarningDismissed },
      }: PayloadAction<
        { variantOverwriteWarningDismissed: boolean } & ClientEnvPayload
      >,
    ) => {
      if (!state[clientEnv][variantName]) {
        state[clientEnv][variantName] = { ...initialConversationState };
      }
      state[clientEnv][variantName].variantOverwriteWarningDismissed =
        variantOverwriteWarningDismissed;
    },
    addUserMessage: (
      state,
      {
        payload: { clientEnv, variantName, message },
      }: PayloadAction<{ message: ChatMessage } & ClientEnvPayload>,
    ) => {
      if (!state[clientEnv][variantName]) {
        state[clientEnv][variantName] = { ...initialConversationState };
      }
      state[clientEnv][variantName].messages.push(message);
    },
    addAgentResponse: (
      state,
      {
        payload: {
          clientEnv,
          variantName,
          agentMessage,
          previousMessageMetadata,
          conversationId,
          agentResponse,
        },
      }: PayloadAction<
        {
          agentMessage: ChatMessage;
          previousMessageMetadata: TurnMetadata;
          conversationId: string;
          agentResponse: AgentResponse;
        } & ClientEnvPayload
      >,
    ) => {
      if (state[clientEnv][variantName].conversationId !== conversationId) {
        return;
      }

      const previousMessage = state[clientEnv][variantName].messages.at(-1);
      if (previousMessage) {
        previousMessage.metadata = previousMessageMetadata;
      }
      state[clientEnv][variantName].messages.push(agentMessage);
      state[clientEnv][variantName].agentResponses.push(agentResponse);
    },
    setIsEnded: (
      state,
      {
        payload: { clientEnv, variantName, isEnded },
      }: PayloadAction<{ isEnded: boolean } & ClientEnvPayload>,
    ) => {
      if (!state[clientEnv][variantName]) {
        state[clientEnv][variantName] = { ...initialConversationState };
      }
      state[clientEnv][variantName].isEnded = isEnded;
    },
    setIsErrored: (
      state,
      {
        payload: { clientEnv, variantName, isErrored },
      }: PayloadAction<{ isErrored: boolean } & ClientEnvPayload>,
    ) => {
      if (!state[clientEnv][variantName]) {
        state[clientEnv][variantName] = { ...initialConversationState };
      }
      state[clientEnv][variantName].isErrored = isErrored;
    },
    setChatMessageDraft: (
      state,
      {
        payload: { clientEnv, variantName, message },
      }: PayloadAction<{ message: string } & ClientEnvPayload>,
    ) => {
      if (!state[clientEnv][variantName]) {
        state[clientEnv][variantName] = { ...initialConversationState };
      }
      state[clientEnv][variantName].messageDraft = message;
    },
    setIsWaitingSlowResponse: (
      state,
      {
        payload: { clientEnv, variantName, isWaiting },
      }: PayloadAction<{ isWaiting: boolean } & ClientEnvPayload>,
    ) => {
      if (!state[clientEnv][variantName]) {
        state[clientEnv][variantName] = { ...initialConversationState };
      }
      state[clientEnv][variantName].isWaitingSlowResponse = isWaiting;
    },
    setEndTurns: (
      state,
      {
        payload,
      }: PayloadAction<
        {
          from: string;
          message: string;
          metadata: TurnMetadata;
        } & ClientEnvPayload
      >,
    ) => {
      if (!state[payload.clientEnv][payload.variantName]) {
        state[payload.clientEnv][payload.variantName] = {
          ...initialConversationState,
        };
      }
      state[payload.clientEnv][payload.variantName].endTurn = {
        text: payload.message,
        from: 'agent',
        metadata: payload.metadata,
      };
    },
    setIsSendingMessage: (
      state,
      {
        payload: { clientEnv, variantName, isSendingMessage },
      }: PayloadAction<
        {
          isSendingMessage: boolean;
        } & ClientEnvPayload
      >,
    ) => {
      if (!state[clientEnv][variantName]) {
        state[clientEnv][variantName] = { ...initialConversationState };
      }
      state[clientEnv][variantName].isSendingMessage = isSendingMessage;
    },
    setConversationAsrLanguageCode: (
      state,
      {
        payload: { clientEnv, variantName, languageCode },
      }: PayloadAction<
        {
          languageCode: string;
        } & ClientEnvPayload
      >,
    ) => {
      if (!state[clientEnv][variantName]) {
        state[clientEnv][variantName] = { ...initialConversationState };
      }
      state[clientEnv][variantName].asrLanguageCode = languageCode;
    },
    setConversationTtsLanguageCode: (
      state,
      {
        payload: { clientEnv, variantName, languageCode },
      }: PayloadAction<
        {
          languageCode: string;
        } & ClientEnvPayload
      >,
    ) => {
      if (!state[clientEnv][variantName]) {
        state[clientEnv][variantName] = { ...initialConversationState };
      }
      state[clientEnv][variantName].ttsLanguageCode = languageCode;
    },
    setDraftArtifactVersions: (
      state,
      {
        payload: { artifactVersion, lambdaDeployment },
      }: PayloadAction<{ artifactVersion: string; lambdaDeployment?: string }>,
    ) => {
      state.draftArtifactVersions = {
        artifact: artifactVersion,
        lambdaDeployment,
      };
    },
    resetDraftArtifactVersions: resetDraftArtifactVersionsHelper,
  },
  extraReducers: builder => {
    builder
      .addCase(resetChat, (state, { payload: { clientEnv, variantName } }) => {
        if (!clientEnv) {
          return { ...initialState };
        }
        if (!variantName) {
          state[clientEnv] = { ...initialState[clientEnv] };
          if (clientEnv === 'draft') {
            resetDraftArtifactVersionsHelper(state);
          }
          return;
        }
        state[clientEnv][variantName] = initialConversationState;
      })
      .addCase(projectInit, () => {
        return { ...initialState };
      });
  },
});

export const {
  addAgentResponse,
  addUserMessage,
  setIsEnded,
  setIsErrored,
  setConversationId,
  setIsLoading,
  setChatMessageDraft,
  setIsWaitingSlowResponse,
  setEndTurns,
  setIsSendingMessage,
  setConversationAsrLanguageCode,
  setConversationTtsLanguageCode,
  setVariantOverwriteWarningDismissed,
  setDraftArtifactVersions,
  resetDraftArtifactVersions,
} = testAgentSlice.actions;

export default testAgentSlice.reducer;
